import React, { useState } from "react";
import FieldHelp from "../help/FieldHelp";

/**
 * Editor for a branch-grouped email schedule, matching the backend shape:
 *   [{ branch, emails: [...], days: [...], windows: [{ start, end, last_sent }] }]
 *
 * - "branch" targets a branch/main node ("ALL" = Main / whole system).
 * - "days"   are the days-of-month the report is sent on (multi-select).
 * - "windows" are 24-hour "HH:MM" time ranges.
 *
 * Fully controlled: it never mutates `value`, it calls `onChange` with the next array.
 */

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// "ALL" shows as "Main (Whole System)"; others show their category, e.g. "Main 1 (MAIN)".
const renderBranchOptions = (branchList, meta = {}) =>
  (branchList || []).map((b) => (
    <option key={b} value={b}>
      {b === "ALL" ? "Main (Whole System)" : meta[b] ? `${b} (${meta[b]})` : b}
    </option>
  ));

// Report period presets (multi-select). Value = number of days of data the
// report covers: Daily = last 1 day, Weekly = 7, 15 Days = 15, Monthly = 30.
const PERIOD_OPTIONS = [
  { label: "Daily", value: 1 },
  { label: "Weekly", value: 7 },
  { label: "15 Days", value: 15 },
  { label: "Monthly", value: 30 },
];

const WEEKDAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const newWindow = () => ({ start: "09:00", end: "17:00", last_sent: null });
const newBranchGroup = () => ({ branch: "ALL", emails: [], days: [1], weekly_day: "Monday", windows: [newWindow()] });

export default function BranchScheduleEditor({
  value,
  onChange,
  onSave,
  saving = false,
  loading = false,
  branches = [],
  branchMeta = {},
  branchesLoading = false,
  styles = {},
  emptyText = "No sources configured yet. Click “Add Source” to create one.",
  onInvalidEmail,
}) {
  const {
    inputGroupStyle = {},
    textInputStyle = {},
    primaryButtonStyle = {},
    actionButtonStyle = {},
  } = styles;

  const [emailInput, setEmailInput] = useState({}); // per-branch transient text

  const groups = Array.isArray(value) ? value : [];

  const mutate = (bidx, fn) =>
    onChange(groups.map((g, i) => (i === bidx ? fn(g) : g)));

  const addBranch = () => onChange([...groups, newBranchGroup()]);
  const removeBranch = (bidx) => onChange(groups.filter((_, i) => i !== bidx));
  const setBranch = (bidx, branch) => mutate(bidx, (g) => ({ ...g, branch }));

  const togglePeriod = (bidx, value) =>
    mutate(bidx, (g) => {
      const currentDays = (Array.isArray(g.days) ? g.days : (g.days ? [g.days] : [])).map(Number);
      const set = new Set(currentDays);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...g, days: Array.from(set).sort((a, b) => a - b) };
    });

  const setWeeklyDay = (bidx, weekly_day) =>
    mutate(bidx, (g) => ({ ...g, weekly_day }));

  const addWindow = (bidx) =>
    mutate(bidx, (g) => ({ ...g, windows: [...g.windows, newWindow()] }));
  const removeWindow = (bidx, widx) =>
    mutate(bidx, (g) => ({ ...g, windows: g.windows.filter((_, j) => j !== widx) }));
  const setWindow = (bidx, widx, field, val) =>
    mutate(bidx, (g) => ({
      ...g,
      windows: g.windows.map((w, j) => (j === widx ? { ...w, [field]: val } : w)),
    }));

  const addEmail = (bidx) => {
    const raw = (emailInput[bidx] || "").trim();
    if (!raw) return;
    if (!isValidEmail(raw)) {
      onInvalidEmail && onInvalidEmail();
      return;
    }
    mutate(bidx, (g) =>
      g.emails.includes(raw) ? g : { ...g, emails: [...g.emails, raw] },
    );
    setEmailInput((prev) => ({ ...prev, [bidx]: "" }));
  };
  const removeEmail = (bidx, email) =>
    mutate(bidx, (g) => ({ ...g, emails: g.emails.filter((x) => x !== email) }));

  if (loading) {
    return <p style={{ color: "#64748b", fontSize: 14 }}>Loading schedule...</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {groups.length === 0 && (
        <p style={{ color: "#94a3b8", fontSize: 14 }}>{emptyText}</p>
      )}

      {groups.map((group, bidx) => (
        <div
          key={bidx}
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: 14,
            background: "#f8fafc",
          }}
        >
          {/* Source row */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label style={{ ...inputGroupStyle, flex: "1 1 220px", minWidth: 180 }}>
              <FieldHelp id="customization.scheduleBranch">Branch</FieldHelp>
              Source
              <select
                value={group.branch || "ALL"}
                onChange={(e) => setBranch(bidx, e.target.value)}
                disabled={branchesLoading}
                style={{ ...textInputStyle, cursor: "pointer" }}
              >
                {renderBranchOptions(branches, branchMeta)}
              </select>
            </label>
            <div style={{ marginLeft: "auto" }}>
              <button
                type="button"
                onClick={() => removeBranch(bidx)}
                style={{ ...actionButtonStyle, color: "#ef4444" }}
              >
                Remove Source
              </button>
            </div>
          </div>

          {/* Recipients */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
              <FieldHelp id="customization.recipients">Recipients</FieldHelp>
            </div>
            {group.emails.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {group.emails.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      background: "#e6f7f5",
                      color: "#0f766e",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmail(bidx, email)}
                      style={{ background: "none", border: "none", color: "#0f766e", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                type="email"
                value={emailInput[bidx] || ""}
                onChange={(e) => setEmailInput((prev) => ({ ...prev, [bidx]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail(bidx);
                  }
                }}
                placeholder="Add recipient email"
                style={{ ...textInputStyle, flex: "1 1 220px", minWidth: 200 }}
              />
              <button
                type="button"
                onClick={() => addEmail(bidx)}
                style={{ ...primaryButtonStyle, padding: "8px 16px" }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Report period */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
              <FieldHelp id="customization.reportPeriod">Report period</FieldHelp>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {PERIOD_OPTIONS.map((opt) => {
                const active = (group.days || []).includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => togglePeriod(bidx, opt.value)}
                    aria-pressed={active}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 999,
                      border: active ? "1px solid #1BA08E" : "1px solid #cbd5e1",
                      background: active ? "#1BA08E" : "#fff",
                      color: active ? "#fff" : "#475569",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Weekly send day selector */}
            {(group.days || []).includes(7) && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#475569" }}>
                  Send weekly on:
                </span>
                <select
                  value={group.weekly_day || "Monday"}
                  onChange={(e) => setWeeklyDay(bidx, e.target.value)}
                  style={{
                    ...textInputStyle,
                    width: "auto",
                    padding: "4px 10px",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#1e293b",
                  }}
                >
                  {WEEKDAY_OPTIONS.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Time windows */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
              <FieldHelp id="customization.timeWindows">Time Windows</FieldHelp>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {group.windows.map((win, widx) => (
                <div key={widx} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                    <FieldHelp id="customization.startTime">Start Time</FieldHelp>
                    <input
                      type="time"
                      className="eff-time-input"
                      value={win.start}
                      onChange={(e) => setWindow(bidx, widx, "start", e.target.value)}
                      onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                      style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                    />
                  </label>
                  <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                    <FieldHelp id="customization.endTime">End Time</FieldHelp>
                    <input
                      type="time"
                      className="eff-time-input"
                      value={win.end}
                      onChange={(e) => setWindow(bidx, widx, "end", e.target.value)}
                      onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                      style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                    />
                  </label>
                  {win.last_sent && (
                    <span style={{ fontSize: 12, color: "#94a3b8", alignSelf: "center" }}>
                      <FieldHelp id="customization.lastSent">Last sent</FieldHelp>: {win.last_sent}
                    </span>
                  )}
                  <div style={{ marginLeft: "auto" }}>
                    <button
                      type="button"
                      onClick={() => removeWindow(bidx, widx)}
                      disabled={group.windows.length <= 1}
                      style={{
                        ...actionButtonStyle,
                        color: group.windows.length <= 1 ? "#cbd5e1" : "#ef4444",
                        cursor: group.windows.length <= 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => addWindow(bidx)}
              style={{
                marginTop: 8,
                background: "none",
                border: "1px dashed #cbd5e1",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                color: "#334155",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              + Add Time Window
            </button>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={addBranch}
          style={{
            background: "none",
            border: "1px dashed #cbd5e1",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 13,
            color: "#334155",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          + Add Source
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={{
            ...primaryButtonStyle,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : "Save Schedule"}
        </button>
      </div>
    </div>
  );
}
